import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useCases } from '../../../../core/di/useCases';
import { useOnboardingAnalytics } from '../../hooks/useOnboardingAnalytics';
import { Text } from '../../../../core/design-system/components/Text';
import { Logo } from '../../../../core/design-system/components/Logo';
import { Icon } from '../../../../core/design-system/components/Icon';
import PhoneStep from '../steps/PhoneStep';
import BirthdayStep from '../steps/BirthdayStep';
import WelcomeStep from '../steps/WelcomeStep';
import ActivitiesStep from '../steps/ActivitiesStep';
import LocationStep from '../steps/LocationStep';
import PhotoStep from '../steps/PhotoStep';
import CompletionStep from '../steps/CompletionStep';
import { useOnboardingValidation } from '../../hooks/useOnboardingValidation';
import { useOnboardingState } from '../../hooks/useOnboardingState';
import './styles.css';

const OnboardingModal = ({ onComplete }) => {
  const { t } = useTranslation('onboarding');
  const { user, refreshUser } = useAuth();
  const { updateOnboardingStep } = useCases;
  const { logStepView, logStepComplete, logOnboardingComplete } = useOnboardingAnalytics();
  const { errors, validateStep, clearError } = useOnboardingValidation();
  const { 
    loading,
    currentStep,
    setCurrentStep,
    onboardingData,
    saveStepData,
    currentStepId
  } = useOnboardingState();
  
  // Masquer la tabbar quand le modal est affiché
  useEffect(() => {
    document.body.classList.add('hide-tabbar');
    
    return () => {
      document.body.classList.remove('hide-tabbar');
    };
  }, []);
  
  // Configuration des étapes
  const steps = useMemo(() => [
    { id: 'welcome', titleKey: 'welcome.title', descriptionKey: 'welcome.description' },
    { id: 'phone', titleKey: 'phone.title', descriptionKey: 'phone.description' },
    { id: 'birthday', titleKey: 'birthday.title', descriptionKey: 'birthday.description' },
    { id: 'activities', titleKey: 'activities.title', descriptionKey: 'activities.description' },
    { id: 'location', titleKey: 'location.title', descriptionKey: 'location.description' },
    { id: 'photo', titleKey: 'photo.title', descriptionKey: 'photo.description' },
    { id: 'completion', titleKey: 'completion.title', descriptionKey: 'completion.description' }
  ], []);

  // Initialiser l'étape courante à partir des données utilisateur
  const [startTime] = useState(Date.now());
  const [stepStartTime, setStepStartTime] = useState(Date.now());

  // Tracking de vue d'étape - Ajout des dépendances manquantes
  useEffect(() => {
    if (!loading && currentStepId) {
      logStepView(currentStepId);
      setStepStartTime(Date.now());
    }
  }, [currentStep, loading, logStepView, currentStepId, setStepStartTime]);

  // Fonction pour revenir à l'étape précédente
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Fonction pour compléter l'onboarding
  const completeOnboarding = async () => {
    try {
      const totalTime = (Date.now() - startTime) / 1000;
      
      await updateOnboardingStep.execute(user.uid, 'completion', {
        onboardingCompleted: true
      });
      
      logOnboardingComplete(steps.length, totalTime);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      onComplete();
      
      try {
        await refreshUser();
      } catch (refreshError) {
        // Ignorer l'erreur silencieusement
      }
      
    } catch (error) {
      console.error('Erreur lors de la complétion de l\'onboarding:', error);
    }
  };

  const handleStepComplete = async (stepId, stepData) => {
    // Validation de l'étape
    if (!validateStep(stepId, stepData)) {
      return false;
    }

    // Nettoyage des données avant sauvegarde
    const cleanData = Object.entries(stepData).reduce((acc, [key, value]) => {
      // Ignorer les événements natifs et les fonctions
      if (value instanceof Event || typeof value === 'function') {
        return acc;
      }
      // Pour les objets, on vérifie qu'ils sont sérialisables
      if (typeof value === 'object' && value !== null) {
        try {
          JSON.stringify(value);
          acc[key] = value;
        } catch (e) {
          console.warn(`Skipping non-serializable value for key: ${key}`);
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});

    const stepDuration = (Date.now() - stepStartTime) / 1000;
    logStepComplete(stepId, {
      ...cleanData,
      duration: stepDuration
    });

    const success = await saveStepData(stepId, cleanData);
    if (success) {
      if (currentStep === steps.length - 1) {
        await completeOnboarding();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
    return success;
  };

  // Fonction simplifiée pour rendre le contenu de l'étape
  const renderStepContent = () => {
    const stepProps = {
      onComplete: (data) => handleStepComplete(currentStepId, data),
      initialValue: onboardingData[currentStepId],
      error: errors[currentStepId],
      onClearError: () => clearError(currentStepId)
    };

    const StepComponent = {
      welcome: WelcomeStep,
      phone: PhoneStep,
      birthday: BirthdayStep,
      activities: ActivitiesStep,
      location: LocationStep,
      photo: PhotoStep,
      completion: CompletionStep
    }[currentStepId];

    return StepComponent ? <StepComponent {...stepProps} /> : null;
  };

  // Récupérer l'étape actuelle de manière sécurisée
  const currentStepData = steps[currentStep] || steps[0];

  if (loading) {
    return <div>Chargement...</div>; // Ou un spinner
  }

  return (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal-container">
        <div className="onboarding-modal-header">
          <div className="header-left">
            {currentStep > 0 && currentStep < steps.length - 1 && 
             currentStepId !== 'birthday' && (
              <button 
                className="back-button"
                onClick={goToPreviousStep}
              >
                <Icon name="arrow-left" size={24} />
              </button>
            )}
            <Logo size={36} />
          </div>
          
          {/* Indicateur d'étape */}
          <div className="step-indicator">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`step-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>
        </div>

        <div className="onboarding-modal-content">
          <div className="onboarding-step-content">
            <Text variant="h2" className="onboarding-step-title">
              {currentStepData && t(currentStepData.titleKey)}
            </Text>
            <Text variant="body1" className="onboarding-step-description">
              {currentStepData && t(currentStepData.descriptionKey)}
            </Text>
            
            {/* Ajout du contenu dynamique de l'étape */}
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal; 